import { call, put, select } from 'redux-saga/effects';

import { GeneratorResult } from '@pressreader/appstore';
import { closePopupAsCancelled, openPopup } from '@pressreader/popups-core';

import { commentsDialogClosed, commentsDialogClosing, commentsDialogOpened, commentsDialogOpening } from './store/actions/common';
import { selectIsDialogOpenedOrOpening, selectPopupId } from './store/selectors/common';
import {
    CommentsDialogOpenParameters,
    CommentsDialogOptions,
    CommentsDialogResult,
    CommentsDialogResultCommentsMetadata,
} from './types/comments.dialog';
import { COMMENTS_DIALOG_NAME } from './constants';

type DialogPopupGeneratorResult = GeneratorResult<CommentsDialogResult>;

function* openCommentsDialogWithOptions(options: CommentsDialogOptions, sagas?: CommentsDialogOpenParameters): DialogPopupGeneratorResult {
    return yield* openPopup<CommentsDialogResultCommentsMetadata>(
        {
            ...options,
            type: 'dialog',
            name: COMMENTS_DIALOG_NAME,
            supportNativeScroll: true,
            persistent: true,
        },
        sagas,
    );
}

export function* openCommentsDialogForArticle(
    {
        articleId,
        issueCidDateKey,
    }: {
        articleId: CommentsDialogOptions['articleId'];
        issueCidDateKey: CommentsDialogOptions['issueCidDateKey'];
    },
    sagas?: CommentsDialogOpenParameters,
): DialogPopupGeneratorResult {
    const options: CommentsDialogOptions = {
        articleId,
        issueCidDateKey,
    };
    return yield call(openCommentsDialogWithOptions, options, sagas);
}

export function* onCommentsDialogOpening(articleId: string) {
    yield put(commentsDialogOpening({ articleId }));
}

export function* onCommentsDialogOpened(popupId: string) {
    yield put(commentsDialogOpened({ popupId }));
}

export function* onCommentsDialogClosing({ popupId }: { popupId: string }): GeneratorResult<boolean> {
    yield put(commentsDialogClosing({ popupId }));
    return true;
}

export function* onCommentsDialogClosed({ popupId }: { popupId: string }) {
    yield put(commentsDialogClosed({ popupId }));
}

export function* onCommentsDialogSagaCancelled(): GeneratorResult<void> {
    const currentPopupIdOrEmptyString: string = yield select(selectPopupId);

    if (!currentPopupIdOrEmptyString) {
        return;
    }

    const isDialogOpenedOrOpening: boolean = yield select(selectIsDialogOpenedOrOpening);

    if (!isDialogOpenedOrOpening) {
        return;
    }

    yield call(onCommentsDialogClosing, {
        popupId: currentPopupIdOrEmptyString,
    });
    yield call(closePopupAsCancelled, {
        id: currentPopupIdOrEmptyString,
    });
    yield call(onCommentsDialogClosed, {
        popupId: currentPopupIdOrEmptyString,
    });
}
